import React from "react"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"
import { Bar } from "react-chartjs-2"
import PropTypes from "prop-types"
import { solve_colors } from "./solve-status-colors"
import { useSelector } from "react-redux"
import { selectSolverStatuses } from "../observations/slice"

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

/**
 * Solver status rendered as a bar chart and grouped by Orbital Regime
 * Here is an example of the solveStatusByOrbitType input message
 * {
 *   "Geostationary Orbit": {
 *     no_satellites: 179,
 *     not_enough_stars: 360,
 *     success: 536,
 *   },
 *   "High Earth Orbit / Cislunar Space": {
 *     no_satellites: 16,
 *     not_enough_stars: 39,
 *     success: 8,
 *   },
 *   "Highly Elliptical Orbit": {
 *     no_satellites: 115,
 *     not_enough_stars: 145,
 *     success: 80,
 *   },
 *   "Low Earth Orbit": {
 *     crash: 4,
 *     no_astrometry_match: 24,
 *     no_satellites: 48,
 *     not_enough_stars: 169,
 *     success: 220,
 *   },
 *   "Medium Earth Orbit": {
 *     crash: 1,
 *     no_satellites: 65,
 *     not_enough_stars: 158,
 *     success: 118,
 *   },
 * }
 *
 * @param solveStatusByOrbitType See example data set above for sample input values
 * @param usePercent Renders the graph as a percent rather than the actual values of each category
 * @returns {false|JSX.Element}
 * @constructor
 */
const SolverStatusBarChart = ({
  solveStatusByOrbitType = {},
  usePercent = false,
}) => {
  if (solveStatusByOrbitType == null) {
    return null
  }

  // get all the statuses and remove the none option
  let solveStatusNames = useSelector(selectSolverStatuses)

  // get all the labels
  const labels = Object.keys(solveStatusByOrbitType)

  /*
  * We need to end up with a structure that looks like this
  *
  *
  {
    "success": {
      "label": "success",
      "data": [
        "49.86",
        "12.70",
        "23.53",
        "47.31",
        "34.50"
      ],
      "backgroundColor": "rgba(50,205,50, 0.5)"
    },
    "no_astrometry_match": {
      "label": "no_astrometry_match",
      "data": [
        0,
        0,
        0,
        "5.16",
        0
      ],
      "backgroundColor": "rgba(235,226,103, 0.5)"
    }, ...
  }
  * Where each element in the data array lines up with the label
  * */
  let solveDataSetArray = solveStatusNames
    .filter((s) => s !== "none")
    .map((name) => {
      const color = solve_colors[name] || [54, 162, 235]
      return {
        label: name,
        data: labels.map((label) => {
          if (usePercent) {
            const totalCount = Object.values(
              solveStatusByOrbitType[label]
            ).reduce((statusCount, r) => r + statusCount, 0)

            return solveStatusByOrbitType[label][name]
              ? (
                  (solveStatusByOrbitType[label][name] / totalCount) *
                  100
                ).toFixed(2)
              : 0
          }

          return solveStatusByOrbitType[label][name]
            ? solveStatusByOrbitType[label][name]
            : 0
        }),
        // pick a nice color
        backgroundColor: `rgba(${color[0]},${color[1]},${color[2]}, 0.5)`,
      }
    })

  // convert this to a map as expected by the mapping lib
  const solveDataSet = solveDataSetArray.reduce((r, st) => {
    r[st.label] = st
    return r
  }, {})

  const data = {
    labels,
    datasets: Object.values(solveDataSet),
  }

  const options = {
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        ticks: {
          beginAtZero: true,
          callback: function (value) {
            return usePercent ? value + "%" : value
          },
        },
      },
    },
    responsive: true,
    plugins: {
      datalabels: {
        display: function (context) {
          // Only display labels if the value is not zero
          return context.dataset.data[context.dataIndex] !== 0
        },
        // color: "white",
      },
      legend: {
        position: "right",
      },
      title: {
        display: true,
        text:
          "Solve status by orbit regime by " +
          (usePercent ? "percentage" : "count"),
      },
    },
  }

  return (
    Object.keys(solveDataSet).length > 0 && (
      <Bar options={options} data={data} />
    )
  )
}

SolverStatusBarChart.propTypes = {
  solveStatusByOrbitType: PropTypes.object, // a map of the count per solve status by orbit type (see example dataset)
  usePercent: PropTypes.bool, // Render as a percent (each orbit type is a single stacked bar)
}

export default SolverStatusBarChart
